import React from 'react';
import { ProgressBarProps } from '../../../types/health-tools';

/**
 * A reusable progress bar component for health metrics
 * 
 * @param value - Current value
 * @param max - Maximum value
 * @param color - Bar color (CSS variable or color code)
 * @param showLabel - Whether to show the percentage label
 * @param labelPosition - Position of the label
 * @param size - Size of the progress bar
 * @param className - Additional CSS classes
 */
const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max,
  color = 'var(--brand-primary)',
  showLabel = false,
  labelPosition = 'right',
  size = 'md',
  className = '',
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  
  // Determine height based on size
  const getHeight = () => {
    switch (size) {
      case 'sm': return 'h-1';
      case 'lg': return 'h-4';
      case 'md':
      default: return 'h-2';
    }
  };
  
  // Generate label based on position
  const renderLabel = () => {
    if (!showLabel) return null;
    
    const labelText = `${Math.round(percentage)}%`;
    
    if (labelPosition === 'inside' && size === 'lg') {
      return (
        <span className="absolute text-xs text-white font-medium" 
              style={{ left: `${percentage > 10 ? percentage / 2 : 10}%` }}>
          {labelText}
        </span>
      );
    }
    
    if (labelPosition === 'top') {
      return (
        <div className="text-xs text-[var(--text-tertiary)] mb-1 text-right">
          {labelText}
        </div>
      );
    }
    
    if (labelPosition === 'right') {
      return (
        <span className="text-xs text-[var(--text-tertiary)] ml-2">
          {labelText}
        </span>
      );
    }
    
    return null;
  };
  
  return (
    <div className={`flex items-center ${className}`}>
      <div className="flex-grow relative">
        {labelPosition === 'top' && renderLabel()}
        <div className={`w-full bg-[var(--background-inset)] rounded-full ${getHeight()}`}>
          <div 
            className={`rounded-full ${getHeight()}`}
            style={{ width: `${percentage}%`, backgroundColor: color }}
          >
            {labelPosition === 'inside' && renderLabel()}
          </div>
        </div>
      </div>
      {labelPosition === 'right' && renderLabel()}
    </div>
  );
};

export default ProgressBar; 